import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { 
  Building2, MapPin, Package, ShoppingCart, Search,
  Loader2, ChevronLeft, ChevronRight, Home, X,
  Clock, AlertCircle, RefreshCw, FileText, Video, ImageIcon,
  Filter, CalendarDays, Play, Download, Film, ExternalLink,
  AlertTriangle, Ban, RotateCcw, Link2, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import { AppHeader } from './AppHeader';
import './BranchListings.css';

const API = process.env.REACT_APP_BACKEND_URL;

function BranchListings({ onBack, onNavigate }) {
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [pendingListings, setPendingListings] = useState([]);
  const [branchName, setBranchName] = useState('');
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [pagination, setPagination] = useState({});
  const [page, setPage] = useState(1);
  const [purchaseModal, setPurchaseModal] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [thumbnails, setThumbnails] = useState({});
  const [thumbsLoading, setThumbsLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingOpen, setPendingOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [fromCache, setFromCache] = useState(false);
  const [searchedListing, setSearchedListing] = useState(null);
  const [searchingWms, setSearchingWms] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [videosModal, setVideosModal] = useState(null); // { listing }
  const scanRetryRef = { current: null };

  const loadListings = useCallback(async (pg = 1, showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      let url = `${API}/api/listings/my-branch?page=${pg}&status=${statusFilter}`;
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      const res = await axios.get(url);
      const data = res.data;
      setListings(data.listings || []);
      setPendingListings(data.pending_listings || []);
      setBranchName(data.branch_name || '');
      setPagination(data.pagination || {});
      setPendingCount(data.pending_count || 0);
      setFromCache(!!data.from_cache);

      if (data.scan_triggered && !data.from_cache) {
        setScanning(true);
        // Poll for scan results at 10s and 25s (WMS scan takes ~20s for 166 pages)
        const retryAt = [10000, 25000];
        for (const delay of retryAt) {
          setTimeout(async () => {
            try {
              const res2 = await axios.get(`${API}/api/listings/my-branch?page=${pg}`);
              const d2 = res2.data;
              if (d2.from_cache || (d2.listings && d2.listings.length > 0)) {
                setListings(d2.listings || []);
                setPendingListings(d2.pending_listings || []);
                setBranchName(d2.branch_name || '');
                setPagination(d2.pagination || {});
                setPendingCount(d2.pending_count || 0);
                setFromCache(!!d2.from_cache);
                setScanning(false);
              }
            } catch { /* silent */ }
          }, delay);
        }
        // Stop scanning indicator after last poll
        setTimeout(() => setScanning(false), 27000);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load listings');
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [statusFilter, dateFrom, dateTo]);

  const handleRefreshScan = async () => {
    setScanning(true);
    try {
      const res = await axios.post(`${API}/api/listings/refresh-scan`);
      toast.success(`Found ${res.data.completed_count} completed, ${res.data.pending_count} pending`);
      await loadListings(1, false);
      setPage(1);
    } catch (err) {
      toast.error('Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const loadThumbnails = useCallback(async (orderList) => {
    if (!orderList.length) return;
    setThumbsLoading(true);

    const orderDates = {};
    orderList.forEach(l => {
      if (l.order_id && l.created_at) orderDates[l.order_id] = l.created_at;
    });
    const orderIds = orderList.map(l => l.order_id).filter(Boolean);

    const fetchThumbs = async () => {
      const res = await axios.post(`${API}/api/listings/thumbnails`, { order_ids: orderIds, order_dates: orderDates });
      const t = res.data.thumbnails || {};
      if (Object.keys(t).length > 0) setThumbnails(prev => ({ ...prev, ...t }));
      return res.data.pending || 0;
    };

    try {
      const pending = await fetchThumbs();
      if (pending > 0) {
        // One retry after 10s for any thumbnails that weren't found in the first scan
        setTimeout(async () => {
          try { await fetchThumbs(); } catch { /* silent */ }
          setThumbsLoading(false);
        }, 10000);
        return;
      }
    } catch { /* silent */ }
    setThumbsLoading(false);
  }, []);

  useEffect(() => { loadListings(page); }, [page, loadListings, statusFilter, dateFrom, dateTo]);

  useEffect(() => {
    if (listings.length > 0) {
      loadThumbnails(listings);
    }
  }, [listings, loadThumbnails]);

  const handlePurchase = async (packageType) => {
    if (!purchaseModal) return;
    setPurchasing(true);
    try {
      await axios.post(`${API}/api/listings/purchase`, {
        order_id: purchaseModal.order_id,
        package_type: packageType,
      });
      toast.success(`${packageType === 'social_go' ? 'Social Go' : 'Social Pro'} purchased!`);
      setPurchaseModal(null);
      loadListings(page, false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purchase failed');
    } finally {
      setPurchasing(false);
    }
  };

  const totalPages = pagination.totalPages || 1;

  // When searching by order ID (all digits), do a direct WMS lookup
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q || !/^\d{5,}$/.test(q)) {
      setSearchedListing(null);
      setSearchingWms(false);
      return;
    }
    // Check if order already exists locally
    const localMatch = listings.find(l => String(l.order_id) === q) ||
                       pendingListings.find(l => String(l.order_id) === q);
    if (localMatch) {
      setSearchedListing(null);
      return;
    }
    // Debounce the WMS lookup
    const timer = setTimeout(async () => {
      setSearchingWms(true);
      try {
        const res = await axios.get(`${API}/api/listings/search/${q}`);
        const found = res.data.listing;
        if (found) {
          setSearchedListing(found);
          // Also fetch thumbnail for this order
          try {
            const thumbRes = await axios.post(`${API}/api/listings/thumbnails`, {
              order_ids: [found.order_id],
              order_dates: found.created_at ? { [found.order_id]: found.created_at } : {},
            });
            const t = thumbRes.data.thumbnails || {};
            if (Object.keys(t).length > 0) setThumbnails(prev => ({ ...prev, ...t }));
          } catch { /* silent */ }
        }
      } catch {
        setSearchedListing(null);
      } finally {
        setSearchingWms(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery, listings, pendingListings]);

  const filtered = searchQuery
    ? listings.filter(l =>
        (l.full_address || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(l.order_id).includes(searchQuery)
      )
    : listings;

  // Include the WMS-searched listing if not already in filtered results
  const displayListings = searchedListing && !filtered.find(l => l.order_id === searchedListing.order_id)
    ? [searchedListing, ...filtered]
    : filtered;

  const filteredPending = searchQuery
    ? pendingListings.filter(l =>
        (l.full_address || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(l.order_id).includes(searchQuery)
      )
    : pendingListings;

  return (
    <div className="branch-listings" data-testid="branch-listings">
      <AppHeader onBack={onBack} />

      <div className="listings-header">
        <div className="listings-header-left">
          <Building2 size={22} />
          <div>
            <h1 className="listings-title">Property Listings</h1>
            {branchName && <span className="listings-branch-name">{branchName}</span>}
          </div>
        </div>
        <div className="listings-header-right">
          <button
            className={`refresh-scan-btn ${scanning ? 'scanning' : ''}`}
            onClick={handleRefreshScan}
            disabled={scanning}
            title="Refresh WMS scan"
            data-testid="refresh-scan-btn"
          >
            <RefreshCw size={15} className={scanning ? 'animate-spin' : ''} />
          </button>
          {pendingCount > 0 && (
            <button
              className={`pending-trigger ${pendingOpen ? 'active' : ''}`}
              onClick={() => setPendingOpen(o => !o)}
              data-testid="pending-drawer-trigger"
            >
              <Clock size={14} />
              <span>{pendingCount} Pending</span>
            </button>
          )}
          <div className={`listings-search-bar ${searchOpen ? 'open' : ''}`}>
            <button
              className="listings-search-trigger"
              onClick={() => { setSearchOpen(o => !o); if (searchOpen && searchQuery) setSearchQuery(''); }}
              data-testid="listings-search-trigger"
            >
              <Search size={18} />
            </button>
            <div className="listings-search-field">
              <input
                type="text"
                className="listings-search-input"
                placeholder="Search address or order ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="listings-search-input"
              />
              {searchQuery && (
                <button className="listings-search-clear" onClick={() => setSearchQuery('')}>
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {scanning && !loading && listings.length > 0 && (
        <div className="scan-banner" data-testid="scan-banner">
          <div className="scan-pulse" />
          <span>Scanning WMS for completed listings...</span>
        </div>
      )}

      {/* Status + Date Filters */}
      <div className="listings-filters" data-testid="listings-filters">
        <div className="listings-status-tabs">
          {[
            { key: 'all', label: 'All' },
            { key: 'completed', label: 'Completed' },
            { key: 'pending', label: 'Pending' },
          ].map(tab => (
            <button
              key={tab.key}
              className={`listings-status-tab ${statusFilter === tab.key ? 'active' : ''}`}
              onClick={() => { setStatusFilter(tab.key); setPage(1); }}
              data-testid={`filter-${tab.key}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="listings-date-filter">
          <button
            className={`listings-date-toggle ${showDateFilter ? 'active' : ''} ${dateFrom || dateTo ? 'has-filter' : ''}`}
            onClick={() => setShowDateFilter(v => !v)}
            data-testid="date-filter-toggle"
          >
            <CalendarDays size={14} />
            {dateFrom || dateTo ? 'Dates' : 'Date Range'}
          </button>
          {showDateFilter && (
            <div className="listings-date-popover" data-testid="date-filter-popover">
              <label className="listings-date-label">
                From
                <input
                  type="date"
                  value={dateFrom}
                  onChange={e => { setDateFrom(e.target.value); setPage(1); }}
                  className="listings-date-input"
                  data-testid="date-from"
                />
              </label>
              <label className="listings-date-label">
                To
                <input
                  type="date"
                  value={dateTo}
                  onChange={e => { setDateTo(e.target.value); setPage(1); }}
                  className="listings-date-input"
                  data-testid="date-to"
                />
              </label>
              {(dateFrom || dateTo) && (
                <button
                  className="listings-date-clear"
                  onClick={() => { setDateFrom(''); setDateTo(''); setPage(1); }}
                  data-testid="date-clear"
                >
                  <X size={12} /> Clear
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="listings-body">
        <div className={`listings-main ${pendingOpen ? 'shifted' : ''}`}>
          <div className="listings-content">
            {loading || (scanning && displayListings.length === 0) ? (
              <div className="listings-loading" data-testid="listings-loader">
                <div className="listings-loader">
                  <div className="listings-loader-ring" />
                  <div className="listings-loader-ring" />
                  <div className="listings-loader-ring" />
                </div>
                <span className="listings-loading-text">Loading property listings</span>
                <span className="listings-loading-sub">Fetching latest from WMS...</span>
              </div>
            ) : displayListings.length === 0 && !searchingWms ? (
              <div className="listings-empty">
                <Home size={48} />
                <p>{searchQuery ? (searchingWms ? 'Searching WMS...' : 'No listings matching your search') : scanning ? 'Scanning WMS...' : 'No listings found for this branch'}</p>
                <span>
                  {searchQuery ? (/^\d{5,}$/.test(searchQuery.trim()) ? 'Looking up order in WMS...' : 'Try a different search or enter an order ID') : fromCache ? 'Try refreshing the scan' : 'Scanning in background...'}
                </span>
                {!searchQuery && !scanning && (
                  <button className="empty-refresh-btn" onClick={handleRefreshScan} data-testid="empty-refresh-btn">
                    <RefreshCw size={14} /> Refresh Scan
                  </button>
                )}
              </div>
            ) : (
              <>
                {(thumbsLoading || searchingWms) && (
                  <div className="thumbs-loading-bar">
                    <div className="thumbs-loading-progress" />
                  </div>
                )}
                <div className="listings-grid" data-testid="listings-grid">
                  {displayListings.map((listing, idx) => (
                    <PropertyCard
                      key={listing.order_id}
                      listing={listing}
                      thumbnail={thumbnails[String(listing.order_id)]}
                      onBuy={() => setPurchaseModal(listing)}
                      onNavigate={onNavigate}
                      onShowVideos={() => setVideosModal({ listing })}
                      delay={idx}
                    />
                  ))}
                </div>
                {totalPages > 1 && !searchQuery && (
                  <div className="listings-pagination" data-testid="listings-pagination">
                    <button className="page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                      <ChevronLeft size={16} /> Prev
                    </button>
                    <span className="page-info">Page {page} of {totalPages}</span>
                    <button className="page-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                      Next <ChevronRight size={16} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Slide-out Pending Orders Panel */}
        <div className={`pending-panel ${pendingOpen ? 'open' : ''}`} data-testid="pending-drawer">
          <div className="pending-panel-header">
            <div className="pending-panel-title">
              <AlertCircle size={16} />
              <div>
                <span className="pending-panel-heading">Pending Orders</span>
                <span className="pending-panel-sub">Awaiting completion</span>
              </div>
            </div>
            <button className="pending-panel-close" onClick={() => setPendingOpen(false)} data-testid="pending-drawer-close">
              <X size={16} />
            </button>
          </div>
          <div className="pending-panel-body">
            {filteredPending.length === 0 ? (
              <div className="pending-empty">
                <Clock size={20} />
                <span>No pending orders</span>
              </div>
            ) : (
              filteredPending.map((listing) => (
                <PendingOrderRow key={listing.order_id} listing={listing} onBuy={() => setPurchaseModal(listing)} />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Backdrop when panel open on mobile */}
      {pendingOpen && <div className="pending-backdrop" onClick={() => setPendingOpen(false)} />}

      {purchaseModal && (
        <div className="purchase-overlay" data-testid="purchase-modal">
          <div className="purchase-modal">
            <button className="purchase-close" onClick={() => setPurchaseModal(null)}><X size={18} /></button>
            <h2>Choose a Package</h2>
            <p className="purchase-address">{purchaseModal.full_address || `Order #${purchaseModal.order_id}`}</p>
            <div className="package-options">
              <button className="package-option package-go" onClick={() => handlePurchase('social_go')} disabled={purchasing} data-testid="buy-social-go">
                <span className="package-badge-large badge-go">Social Go</span>
                <span className="package-price">&pound;20</span>
                <span className="package-desc">Essential social media images</span>
                {purchasing ? <Loader2 size={16} className="animate-spin" /> : <ShoppingCart size={16} />}
                <span className="package-cta">Buy Now</span>
              </button>
              <button className="package-option package-pro" onClick={() => handlePurchase('social_pro')} disabled={purchasing} data-testid="buy-social-pro">
                <span className="package-badge-large badge-pro">Social Pro</span>
                <span className="package-price">&pound;30</span>
                <span className="package-desc">Premium social content with video</span>
                {purchasing ? <Loader2 size={16} className="animate-spin" /> : <ShoppingCart size={16} />}
                <span className="package-cta">Buy Now</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {videosModal && (
        <VideosModal
          listing={videosModal.listing}
          onClose={() => setVideosModal(null)}
          onOpenEditor={({ productFilter, productName, excludeNames, orderedNames } = {}) => {
            try {
              sessionStorage.setItem('prefillWmsJobId', String(videosModal.listing.order_id));
              if (productFilter) {
                sessionStorage.setItem('prefillWmsProductFilter', productFilter);
              } else {
                sessionStorage.removeItem('prefillWmsProductFilter');
              }
              if (productName) {
                sessionStorage.setItem('prefillWmsProductLabel', productName);
              } else {
                sessionStorage.removeItem('prefillWmsProductLabel');
              }
              if (excludeNames && excludeNames.length) {
                sessionStorage.setItem('prefillWmsExcludeNames', JSON.stringify(excludeNames));
              } else {
                sessionStorage.removeItem('prefillWmsExcludeNames');
              }
              if (orderedNames && orderedNames.length) {
                sessionStorage.setItem('prefillWmsOrderedNames', JSON.stringify(orderedNames));
              } else {
                sessionStorage.removeItem('prefillWmsOrderedNames');
              }
            } catch (_) {}
            setVideosModal(null);
            if (onNavigate) onNavigate('video');
          }}
        />
      )}
    </div>
  );
}


function PropertyCard({ listing, thumbnail, onBuy, onNavigate, onShowVideos, delay = 0 }) {
  const address = listing.full_address || '';
  const products = listing.products || [];
  const createdAt = listing.created_at || '';
  const formattedDate = createdAt ? new Date(createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  const maxProgress = listing.max_progress || 0;
  const isProcessing = maxProgress >= 300;
  const isCompleted = maxProgress >= 400;
  // Video icon only lights up when the order actually includes a video product
  const hasVideo = (listing.video_products || []).length > 0 ||
    products.some((p) => /video|drone|sneak peek|showreel/i.test(typeof p === 'string' ? p : (p?.name || '')));
  const canOpenVideos = isProcessing && hasVideo;

  return (
    <div className="property-card" data-testid={`property-card-${listing.order_id}`}>
      <div className="property-thumbnail">
        <div className="property-thumbnail-placeholder">
          <img src="/nichecom-logo.png" alt="" className="placeholder-logo" />
        </div>
        {thumbnail && (
          <img
            src={thumbnail}
            alt={address}
            loading="lazy"
            className="thumb-fade-in"
            style={{ animationDelay: `${delay * 60}ms` }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
        <div className="property-overlay-top">
          {isCompleted
            ? <span className="status-badge status-completed" data-testid={`status-${listing.order_id}`}>Completed</span>
            : isProcessing
              ? <span className="status-badge status-processing" data-testid={`status-${listing.order_id}`}>Processing</span>
              : <span className="status-badge status-active" data-testid={`status-${listing.order_id}`}>Active</span>
          }
          {listing.branch_name && <span className="branch-name-badge">{listing.branch_name}</span>}
        </div>
        <div className="property-overlay-bottom">
          <span className="order-id-badge">#{listing.order_id}</span>
        </div>
      </div>

      <div className="property-card-body">
        <div className="property-address">
          <MapPin size={14} />
          <span>{address || 'Address unavailable'}</span>
        </div>
        {products.length > 0 && (
          <div className="property-products">
            {products.slice(0, 3).map((p, i) => (
              <span key={i} className="product-tag"><Package size={10} /> {p}</span>
            ))}
          </div>
        )}
        {formattedDate && <span className="property-date">{formattedDate}</span>}
      </div>

      <div className="property-card-footer">
        <button className="property-buy-btn" onClick={onBuy} data-testid={`buy-btn-${listing.order_id}`}>
          <ShoppingCart size={14} /> Buy Package
        </button>
        <div className="property-tool-icons">
          <button
            className="tool-icon-btn tool-icon-script"
            onClick={() => onNavigate && onNavigate('script')}
            title="Generate Script"
            data-testid={`script-btn-${listing.order_id}`}
          >
            <FileText size={15} />
          </button>
          <button
            className={`tool-icon-btn tool-icon-video ${canOpenVideos ? '' : 'tool-icon-disabled'}`}
            onClick={() => {
              if (!canOpenVideos) return;
              if (onShowVideos) {
                onShowVideos();
              } else if (onNavigate) {
                try {
                  sessionStorage.setItem('prefillWmsJobId', String(listing.order_id));
                } catch (_) {}
                onNavigate('video');
              }
            }}
            title={canOpenVideos ? 'View videos for this job' : hasVideo ? 'Available when processing' : 'No video product on this order'}
            disabled={!canOpenVideos}
            data-testid={`video-btn-${listing.order_id}`}
          >
            <Video size={15} />
          </button>
          <button
            className={`tool-icon-btn tool-icon-image ${isCompleted ? '' : 'tool-icon-disabled'}`}
            onClick={() => isCompleted && onNavigate && onNavigate('social')}
            title={isCompleted ? 'Create Social Image' : 'Available when completed'}
            disabled={!isCompleted}
            data-testid={`image-btn-${listing.order_id}`}
          >
            <ImageIcon size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}

export const normalizeClipStem = (name = '') => {
  let stem = name.toLowerCase().replace(/\.[a-z0-9]+$/i, '');
  stem = stem
    .replace(/\s*\(\d+\)\s*$/, '')
    .replace(/[\s_-]+copy\s*\d*$/, '')
    .replace(/[\s_-]+\d$/, '');
  return stem.trim();
};

export function VideosModal({ listing, onClose, onOpenEditor }) {
  const [loading, setLoading] = useState(true);
  const [finalVideo, setFinalVideo] = useState(null);
  const [rawClips, setRawClips] = useState([]);
  const [error, setError] = useState(null);
  const [previewClip, setPreviewClip] = useState(null);
  const [productFilter, setProductFilter] = useState('all');
  const [disabledIds, setDisabledIds] = useState(() => new Set());
  const [dupGroups, setDupGroups] = useState([]);
  const [dupChecking, setDupChecking] = useState(false);
  const [sortLink, setSortLink] = useState(null);
  const [creatingLink, setCreatingLink] = useState(false);
  const videoRefs = useRef({});

  const clipKey = (c) => String(c.id ?? c.name);
  const toggleClip = (c) => setDisabledIds(prev => {
    const next = new Set(prev);
    const k = clipKey(c);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setProductFilter('all');
    axios.get(`${API}/api/listings/${listing.order_id}/videos`)
      .then(res => {
        if (cancelled) return;
        setFinalVideo(res.data.final_video || null);
        setRawClips(res.data.raw_clips || []);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err.response?.data?.detail || 'Could not load videos for this listing.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [listing.order_id]);

  // Exact-duplicate pre-check: the backend fingerprints each clip file
  // (size + first-64KB hash) so only byte-identical captures group together.
  useEffect(() => {
    if (!rawClips.length) { setDupGroups([]); return; }
    let cancelled = false;
    setDupChecking(true);
    axios.post(`${API}/api/listings/${listing.order_id}/videos/dupe-check`)
      .then(res => { if (!cancelled) setDupGroups(res.data.duplicate_groups || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setDupChecking(false); });
    return () => { cancelled = true; };
  }, [rawClips, listing.order_id]);

  // Client sort link status for this order
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/editor-hub/clip-sort/by-order/${listing.order_id}`)
      .then(res => { if (!cancelled) setSortLink(res.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [listing.order_id]);

  const createSortLink = async () => {
    setCreatingLink(true);
    try {
      let slug = sortLink?.exists ? sortLink.slug : null;
      if (!slug || sortLink?.status === 'saved') {
        const names = rawClips.filter(c => !disabledIds.has(clipKey(c))).map(c => c.name).filter(Boolean);
        const res = await axios.post(`${API}/api/editor-hub/clip-sort`, {
          order_id: listing.order_id,
          clip_names: names,
          address: listing.full_address || '',
        });
        slug = res.data.slug;
        setSortLink({ exists: true, slug, status: 'pending', saved_order: null });
      }
      const url = `${window.location.origin}/clipsort/${slug}`;
      try { await navigator.clipboard.writeText(url); toast.success('Client sort link copied to clipboard'); }
      catch (_) { toast.success(`Client sort link: ${url}`); }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create sort link');
    } finally {
      setCreatingLink(false);
    }
  };

  const handlePreviewHover = (id, action) => {
    const el = videoRefs.current[id];
    if (!el) return;
    if (action === 'play') {
      try { el.currentTime = 0; el.play().catch(() => {}); } catch (_) {}
    } else {
      try { el.pause(); el.currentTime = 0; } catch (_) {}
    }
  };

  // Group clips by product_name so users can filter Portrait / Landscape / Drone
  // when an order has more than one video product type.
  const productGroups = (() => {
    const groups = new Map();
    rawClips.forEach(c => {
      const key = (c.product_name || 'Other').trim() || 'Other';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(c);
    });
    return Array.from(groups.entries()).map(([name, clips]) => ({ name, clips }));
  })();

  const hasMultipleProducts = productGroups.length > 1;
  const visibleClips = productFilter === 'all'
    ? rawClips
    : rawClips.filter(c => (c.product_name || 'Other') === productFilter);

  // Exact duplicates flagged by the server fingerprint check
  const dupIds = new Set(dupGroups.flat().map(String));
  const visibleDupCount = visibleClips.filter(c => dupIds.has(clipKey(c))).length;
  const excludedNames = rawClips.filter(c => disabledIds.has(clipKey(c))).map(c => c.name).filter(Boolean);

  // One click: keep the first copy in each duplicate group, switch off the rest
  const disableDuplicateCopies = () => {
    setDisabledIds(prev => {
      const next = new Set(prev);
      dupGroups.forEach(g => g.slice(1).forEach(id => next.add(String(id))));
      return next;
    });
  };

  // Compact label for the filter pill — extracts orientation if present, else uses the full name
  const labelFor = (name) => {
    const m = name.match(/\(([^)]+)\)/);
    if (m) return m[1];
    return name.replace(/^Property\s+/i, '').trim() || name;
  };

  const totalClips = rawClips.length + (finalVideo ? 1 : 0);
  const address = listing.full_address || `Order #${listing.order_id}`;

  return (
    <div className="videos-modal-overlay" data-testid="videos-modal" onClick={onClose}>
      <div className="videos-modal" onClick={(e) => e.stopPropagation()}>
        <button className="videos-modal-close" onClick={onClose} data-testid="videos-modal-close"><X size={18} /></button>

        <div className="videos-modal-header">
          <div className="videos-modal-icon"><Film size={20} /></div>
          <div className="videos-modal-titles">
            <h2 className="videos-modal-title">Videos for this listing</h2>
            <p className="videos-modal-address">{address}</p>
            {!loading && !error && (
              <span className="videos-modal-count">
                {totalClips === 0 ? 'No videos yet' : `${totalClips} video${totalClips === 1 ? '' : 's'} from WMS`}
              </span>
            )}
            {!loading && !error && rawClips.length > 0 && (
              <div className="videos-sortlink-row">
                {sortLink?.status === 'saved' && (
                  <span className="videos-sortlink-saved" data-testid="videos-sortlink-saved">
                    <CheckCircle2 size={12} /> Client order saved
                  </span>
                )}
                {sortLink?.exists && sortLink.status !== 'saved' && (
                  <span className="videos-sortlink-pending" data-testid="videos-sortlink-pending">
                    <Clock size={12} /> Awaiting client sort
                  </span>
                )}
                <button
                  className="videos-sortlink-btn"
                  onClick={createSortLink}
                  disabled={creatingLink}
                  data-testid="videos-sortlink-btn"
                  title="Send the client a link where they drag clips into their preferred order"
                >
                  <Link2 size={12} />
                  {creatingLink ? 'Creating…' : sortLink?.exists && sortLink.status !== 'saved' ? 'Copy client sort link' : 'Client sort link'}
                </button>
              </div>
            )}
          </div>
          <button
            className="videos-modal-editor-btn"
            onClick={() => onOpenEditor({
              productFilter: productFilter === 'all' ? null : labelFor(productFilter).toLowerCase(),
              productName: productFilter === 'all' ? null : productFilter,
              excludeNames: excludedNames,
              orderedNames: sortLink?.status === 'saved' ? sortLink.saved_order : null,
            })}
            data-testid="videos-modal-open-editor"
            title="Open these clips in the Video Editor"
          >
            <ExternalLink size={14} />
            {hasMultipleProducts && productFilter !== 'all'
              ? `Open ${labelFor(productFilter)} in Editor`
              : 'Open in Editor'}
            {excludedNames.length > 0 && (
              <span className="videos-editor-excluded" data-testid="videos-editor-excluded-count">
                {excludedNames.length} off
              </span>
            )}
          </button>
        </div>

        <div className="videos-modal-body">
          {loading && (
            <div className="videos-modal-loading">
              <Loader2 size={28} className="animate-spin" />
              <span>Loading videos from WMS...</span>
            </div>
          )}

          {!loading && error && (
            <div className="videos-modal-empty">
              <AlertCircle size={24} />
              <span>{error}</span>
            </div>
          )}

          {!loading && !error && totalClips === 0 && (
            <div className="videos-modal-empty">
              <Film size={32} />
              <span>No videos have been uploaded for this job yet.</span>
              <small>Once the videographer uploads to WMS they'll appear here.</small>
            </div>
          )}

          {!loading && !error && finalVideo && (
            <div className="videos-modal-section">
              <div className="videos-section-label">
                <span className="videos-section-dot" />
                Final Delivered Video
              </div>
              <div className="videos-final-card">
                <video
                  className="videos-final-player"
                  src={finalVideo.url}
                  controls
                  preload="metadata"
                  data-testid="videos-modal-final-video"
                />
                <div className="videos-final-meta">
                  <span className="videos-meta-name">{finalVideo.name}</span>
                  <span className="videos-meta-product">{finalVideo.product_name}</span>
                  <div className="videos-final-actions">
                    <a
                      href={finalVideo.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="videos-action-btn videos-action-open"
                      data-testid="videos-modal-final-open"
                    >
                      <Play size={13} /> Open
                    </a>
                    <a
                      href={finalVideo.url}
                      download={finalVideo.name}
                      className="videos-action-btn videos-action-download"
                      data-testid="videos-modal-final-download"
                    >
                      <Download size={13} /> Download
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!loading && !error && rawClips.length > 0 && (
            <div className="videos-modal-section">
              <div className="videos-section-label">
                <span className="videos-section-dot videos-section-dot-amber" />
                Raw Clips <span className="videos-section-count">{visibleClips.length}{hasMultipleProducts ? ` / ${rawClips.length}` : ''}</span>
              </div>

              {hasMultipleProducts && (
                <div className="videos-product-filter" data-testid="videos-product-filter">
                  <span className="videos-filter-label">Show</span>
                  <button
                    type="button"
                    className={`videos-filter-pill ${productFilter === 'all' ? 'is-active' : ''}`}
                    onClick={() => setProductFilter('all')}
                    data-testid="videos-filter-all"
                  >
                    All <span className="videos-filter-count">{rawClips.length}</span>
                  </button>
                  {productGroups.map(g => (
                    <button
                      type="button"
                      key={g.name}
                      className={`videos-filter-pill ${productFilter === g.name ? 'is-active' : ''}`}
                      onClick={() => setProductFilter(g.name)}
                      data-testid={`videos-filter-${labelFor(g.name).toLowerCase()}`}
                    >
                      {labelFor(g.name)} <span className="videos-filter-count">{g.clips.length}</span>
                    </button>
                  ))}
                </div>
              )}

              {dupChecking && (
                <div className="videos-dup-checking" data-testid="videos-dup-checking">
                  <Loader2 size={13} className="animate-spin" /> Checking for exact duplicate captures…
                </div>
              )}
              {!dupChecking && visibleDupCount > 0 && (
                <div className="videos-dup-notice" data-testid="videos-dup-notice">
                  <AlertTriangle size={14} />
                  <span>
                    <strong>{visibleDupCount} clip{visibleDupCount === 1 ? '' : 's'}</strong> are exact duplicate captures
                    (byte-identical files) — outlined in red.
                  </span>
                  <button
                    className="videos-dup-disable-btn"
                    onClick={disableDuplicateCopies}
                    data-testid="videos-dup-disable-all"
                  >
                    <Ban size={12} /> Turn off duplicate copies
                  </button>
                </div>
              )}

              <div className="videos-clips-grid">
                {visibleClips.map((clip, idx) => {
                  const isDup = dupIds.has(clipKey(clip));
                  const isOff = disabledIds.has(clipKey(clip));
                  return (
                  <div
                    className={`videos-clip-card ${isDup ? 'is-duplicate' : ''} ${isOff ? 'is-disabled' : ''}`}
                    key={clip.id || idx}
                    data-testid={`videos-modal-clip-${idx}`}
                    onMouseEnter={() => !isOff && handlePreviewHover(clip.id, 'play')}
                    onMouseLeave={() => handlePreviewHover(clip.id, 'pause')}
                  >
                    <div className="videos-clip-thumb">
                      <video
                        ref={(el) => { if (el) videoRefs.current[clip.id] = el; }}
                        src={clip.url}
                        muted
                        playsInline
                        preload="metadata"
                        className="videos-clip-video"
                      />
                      {isDup && (
                        <span className="videos-clip-dup-badge" data-testid={`videos-clip-dup-${idx}`}>
                          <AlertTriangle size={10} /> Duplicate
                        </span>
                      )}
                      {isOff && (
                        <span className="videos-clip-off-badge" data-testid={`videos-clip-off-${idx}`}>
                          <Ban size={10} /> Excluded
                        </span>
                      )}
                      <button
                        className="videos-clip-play"
                        onClick={() => setPreviewClip(clip)}
                        title="Play full size"
                      >
                        <Play size={16} />
                      </button>
                    </div>
                    <div className="videos-clip-meta">
                      <span className="videos-clip-name" title={clip.name}>{clip.name}</span>
                      <div className="videos-clip-actions">
                        <button
                          className={`videos-clip-toggle ${isOff ? 'is-off' : ''}`}
                          onClick={(e) => { e.stopPropagation(); toggleClip(clip); }}
                          title={isOff ? 'Include this clip in the editor' : 'Exclude this clip from the editor'}
                          data-testid={`videos-clip-toggle-${idx}`}
                        >
                          {isOff ? <RotateCcw size={12} /> : <Ban size={12} />}
                        </button>
                        <a
                          href={clip.url}
                          download={clip.name}
                          className="videos-clip-download"
                          title="Download clip"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Download size={12} />
                        </a>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {previewClip && (
        <div
          className="videos-preview-overlay"
          onClick={() => setPreviewClip(null)}
          data-testid="videos-modal-preview-overlay"
        >
          <video
            src={previewClip.url}
            controls
            autoPlay
            className="videos-preview-player"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}




function PendingOrderRow({ listing, onBuy }) {
  const address = listing.full_address || 'Address unavailable';
  const createdAt = listing.created_at || '';
  const formattedDate = createdAt ? new Date(createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';
  const products = listing.products || [];

  return (
    <div className="pending-row" data-testid={`pending-row-${listing.order_id}`}>
      <div className="pending-row-info">
        <div className="pending-row-top">
          <span className="pending-row-id">#{listing.order_id}</span>
          {formattedDate && <span className="pending-row-date">{formattedDate}</span>}
        </div>
        <span className="pending-row-address">{address}</span>
        {products.length > 0 && (
          <span className="pending-row-products">{products.join(', ')}</span>
        )}
      </div>
      <div className="pending-row-action">
        <button className="pending-buy-btn" onClick={onBuy} data-testid={`pending-buy-${listing.order_id}`}>
          <ShoppingCart size={12} /> Buy
        </button>
      </div>
    </div>
  );
}


export default BranchListings;
